/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import {
  Calendar,
  User,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
} from "lucide-react";
import Pagination from "../Pagination";

const WagePaymentsTable = ({ payments = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Pagination
  const totalPages = Math.ceil(payments.length / itemsPerPage);
  const paginatedPayments = payments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
      case "paid":
        return "bg-emerald-600 text-white border-emerald-700 dark:bg-emerald-700 dark:text-white dark:border-emerald-800";
      case "pending":
        return "bg-yellow-600 text-white border-yellow-700 dark:bg-yellow-700 dark:text-white dark:border-yellow-800";
      case "processing":
        return "bg-blue-600 text-white border-blue-700 dark:bg-blue-700 dark:text-white dark:border-blue-800";
      case "failed":
        return "bg-red-600 text-white border-red-700 dark:bg-red-700 dark:text-white dark:border-red-800";
      default:
        return "bg-gray-600 text-white border-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
      case "paid":
        return <CheckCircle size={14} />;
      case "pending":
        return <Clock size={14} />;
      case "processing":
        return <Clock size={14} />;
      case "failed":
        return <XCircle size={14} />;
      default:
        return <AlertCircle size={14} />;
    }
  };

  return (
    <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
        <h2 className="text-xl font-bold text-text dark:text-dark-text">
          Wage Payments
        </h2>
        <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
          Platform revenue from caregiver contracts (20% of the amount remaining after Chapa&apos;s fee)
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-6 py-4 font-bold tracking-wider">Client</th>
              <th className="px-6 py-4 font-bold tracking-wider">Caregiver</th>
              <th className="px-6 py-4 font-bold tracking-wider">
                Gross Amount
              </th>
              <th className="px-6 py-4 font-bold tracking-wider">
                Platform Share (20% of net)
              </th>
              <th className="px-6 py-4 font-bold tracking-wider">
                Caregiver Amount (80% of net)
              </th>
              <th className="px-6 py-4 font-bold tracking-wider">Status</th>
              <th className="px-6 py-4 font-bold tracking-wider">Period</th>
              <th className="px-6 py-4 font-bold tracking-wider">Paid Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {paginatedPayments.length > 0 ? (
              paginatedPayments.map((payment) => {
                const grossAmount = Number(
                  payment.grossAmount ?? payment.amount ?? 0,
                );
                const platformFee = Number(
                  payment.platformAmount ?? payment.platformFee ?? 0,
                );
                const caregiverAmount = Number(payment.caregiverAmount ?? 0);

                return (
                  <tr
                    key={payment.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        <span className="font-medium text-text dark:text-dark-text">
                          {payment.user?.name || payment.clientName || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        <span className="font-medium text-text dark:text-dark-text">
                          {payment.caregiver?.name ||
                            payment.caregiverName ||
                            "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-text dark:text-dark-text">
                          Br {grossAmount.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-blue-600 dark:text-blue-400">
                        Br {platformFee.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        Br {caregiverAmount.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyle(
                          payment.status,
                        )}`}
                      >
                        {getStatusIcon(payment.status)}
                        {payment.status || "Unknown"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} className="text-gray-400" />
                        <span className="text-gray-600 dark:text-dark-muted">
                          {payment.period
                            ? new Date(payment.period).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                },
                              )
                            : payment.month
                              ? new Date(payment.month).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                  },
                                )
                              : "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 dark:text-dark-muted">
                        {payment.paidAt || payment.createdAt
                          ? new Date(
                              payment.paidAt || payment.createdAt,
                            ).toLocaleDateString()
                          : "-"}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                >
                  No wage payments found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {payments.length > itemsPerPage && (
        <div className="border-t border-gray-200 dark:border-dark-border bg-gray-50/50 dark:bg-gray-900/50 p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={payments.length}
          />
        </div>
      )}
    </div>
  );
};

export default WagePaymentsTable;
